import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackTermsEvent } from '@/helpers/analytics';
import { getStoredEventSlug } from '@/helpers/eventSlug';
import { clearOnboardingGuest } from '@/helpers/onboardingGuest';
import { setOnboardingSessionId } from '@/helpers/onboardingSession';
import { navigateToError } from '@/helpers/navigateToError';
import { parseTermsNavigationState } from '@/Pages/Terms/types/termsLocationState';
import { api, ApiError } from '@/basedata/apiCall';
import type { ConsentFormState } from '@/types/consent';

const FALLBACK_NEXT_STEP = '/selfie';
const TERMS_PATH = '/terms';

const INITIAL_FORM_STATE: ConsentFormState = {
  acceptedTerms: false,
  acceptedPrivacy: false,
  acceptedMarketing: false,
};

export type ConsentSubmitResult = { nextStep: string; sessionId: string }

export type UseConsentFormResult = {
  form: ConsentFormState;
  isCtaEnabled: boolean;
  isSubmitting: boolean;
  setAcceptedTerms: (value: boolean) => void;
  setAcceptedPrivacy: (value: boolean) => void;
  setAcceptedMarketing: (value: boolean) => void;
  submit: () => Promise<ConsentSubmitResult | null>;
};

export function useConsentForm(): UseConsentFormResult {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState<ConsentFormState>(
    () => parseTermsNavigationState(location.state) ?? INITIAL_FORM_STATE,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setAcceptedTerms = useCallback((value: boolean) => {
    setForm((current) => ({ ...current, acceptedTerms: value }));
  }, []);

  const setAcceptedPrivacy = useCallback((value: boolean) => {
    setForm((current) => ({ ...current, acceptedPrivacy: value }));
  }, []);

  const setAcceptedMarketing = useCallback((value: boolean) => {
    setForm((current) => ({ ...current, acceptedMarketing: value }));
  }, []);

  const isCtaEnabled = form.acceptedTerms && form.acceptedPrivacy;

  const submit = useCallback(async (): Promise<ConsentSubmitResult | null> => {
    if (!form.acceptedTerms || !form.acceptedPrivacy) {
      return null;
    }

    setIsSubmitting(true);

    void trackTermsEvent({
      name: 'terms_submit_click',
      params: { accepted_marketing: form.acceptedMarketing },
    });

    const acceptedAt = new Date().toISOString();

    // Modo evento (convite foto.identila.com.br/<slug>): o aceite carrega o
    // eventSlug e o service escopa a sessão inteira ao tenant/evento.
    const eventSlug = getStoredEventSlug();

    try {
      const response = await api.consent.accept({
        acceptedTerms: form.acceptedTerms,
        acceptedPrivacy: form.acceptedPrivacy,
        acceptedMarketing: form.acceptedMarketing,
        acceptedAt,
        ...(eventSlug !== null ? { eventSlug } : {}),
      });

      const nextStep = response.nextStep.trim() || FALLBACK_NEXT_STEP;
      setOnboardingSessionId(response.sessionId);
      // Sessão nova = pessoa nova até o /identify ou o /invite dizerem quem é.
      // O convidado guardado era da sessão ANTERIOR desta aba (computador
      // compartilhado): mantê-lo pulava a confirmação e o cadastro de quem
      // está na frente da tela.
      clearOnboardingGuest();
      void trackTermsEvent({
        name: 'terms_submit_success',
        params: { next_step: nextStep },
      });
      return { nextStep, sessionId: response.sessionId };
    } catch (error) {
      void trackTermsEvent({ name: 'terms_submit_error' });
      navigateToError(navigate, {
        error,
        originPath: TERMS_PATH,
        originState: { form },
        ...(error instanceof ApiError ? { errorCode: String(error.status) } : {}),
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [form, navigate]);

  return {
    form,
    isCtaEnabled,
    isSubmitting,
    setAcceptedTerms,
    setAcceptedPrivacy,
    setAcceptedMarketing,
    submit,
  };
}
